/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AgentChatIcon from '../icons/ai-chat.svg?react';
import { useAgentPanel } from './AgentPanelContext';

/**
 * Vertically rotated tab that toggles the agent side panel. It is rendered as a
 * child of the sliding panel container and hangs off that container's left edge
 * (translateX(-100%)), so the single container transform moves the tab and the
 * drawer together — they never desync, even on the very first open. Hidden in
 * full screen, and on mobile while the (full-width) panel is open.
 */
function AgentEdgeTab() {
  const { isAgentFullScreen, isAgentPanelOpen, toggleAgentPanel } = useAgentPanel();

  if (isAgentFullScreen) return null;

  return (
    <ButtonBase
      aria-label={isAgentPanelOpen ? 'Close PA Agent panel' : 'Open PA Agent panel'}
      data-testid="agent-edge-tab"
      onClick={toggleAgentPanel}
      sx={(theme) => ({
        position: 'absolute',
        // Offset from the container top (which sits under the 48px app bar), so
        // the tab hangs a comfortable distance down the page.
        top: 36,
        left: 0,
        transform: 'translateX(-100%)',
        // On mobile the panel spans the full viewport, leaving no left edge to
        // ride, so hide the tab while it is open.
        display: { xs: isAgentPanelOpen ? 'none' : 'flex', md: 'flex' },
        flexDirection: 'column',
        alignItems: 'center',
        rowGap: 0.75,
        px: 0.5,
        py: 1.25,
        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,
        color: theme.palette.primary.contrastText,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.85)
            : theme.palette.primary.main,
        boxShadow: theme.shadows[4],
        '&:hover': {
          bgcolor:
            theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark,
        },
      })}
    >
      <AgentChatIcon width={18} height={18} aria-hidden />
      <Typography
        variant="caption"
        sx={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        Agent
      </Typography>
    </ButtonBase>
  );
}

export default AgentEdgeTab;
